import { useState, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  coil_id: "",
  grade: "",
  weight_kg: "",
  thickness_mm: "",
  width_mm: "",
  supplier: "",
  date_received: new Date().toISOString().slice(0, 10),
  notes: "",
};

const Field = ({ id, label, error, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold tracking-wide uppercase text-slate-600">
      {label}
    </Label>
    {children}
    {error && <p className="text-xs text-red-600" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

const CoilForm = ({ initialBarcode = "", onSubmit, submitting = false }) => {
  const [form, setForm] = useState({ ...empty, coil_id: initialBarcode });
  const [errors, setErrors] = useState({});

  // Load active grades from Masters (single source of truth — no hardcoded list)
  const { data: grades = [] } = useQuery({
    queryKey: ["masters", "rm_grade", "active"],
    queryFn: async () => {
      const { data } = await axios.get(
        `${API}/masters?master_type=rm_grade&status=active`,
        { withCredentials: true },
      );
      return (Array.isArray(data) ? data : []).map((m) => m.name).filter(Boolean).sort();
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (initialBarcode) {
      setForm((p) => ({ ...p, coil_id: initialBarcode }));
    }
  }, [initialBarcode]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.coil_id.trim()) e.coil_id = "Barcode required";
    if (!form.grade) e.grade = "Grade required";
    if (!form.weight_kg || Number(form.weight_kg) <= 0) e.weight_kg = "Weight required";
    if (!form.thickness_mm || Number(form.thickness_mm) <= 0) e.thickness_mm = "Thickness required";
    if (!form.width_mm || Number(form.width_mm) <= 0) e.width_mm = "Width required";
    if (!form.supplier.trim()) e.supplier = "Supplier required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const payload = {
      ...form,
      weight_kg: Number(form.weight_kg),
      thickness_mm: Number(form.thickness_mm),
      width_mm: Number(form.width_mm),
    };
    onSubmit(payload, () => setForm({ ...empty, coil_id: "" }));
  };

  return (
    <form onSubmit={handleSubmit} data-testid="coil-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="coil_id" label="SR RM ID (QR code)" error={errors.coil_id}>
          <Input
            id="coil_id"
            data-testid="coil-id-input"
            value={form.coil_id}
            onChange={(e) => set("coil_id", e.target.value)}
            placeholder="Scan or type barcode"
            className="font-mono-data"
          />
        </Field>

        <Field id="grade" label="Grade" error={errors.grade}>
          <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
            <SelectTrigger id="grade" data-testid="grade-select">
              <SelectValue placeholder={grades.length ? "Select grade" : "No grades in Masters — add one first"} />
            </SelectTrigger>
            <SelectContent>
              {grades.map((g) => (
                <SelectItem key={g} value={g} data-testid={`grade-option-${g}`}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field id="weight_kg" label="Weight (kg)" error={errors.weight_kg}>
          <Input
            id="weight_kg"
            data-testid="weight-input"
            type="number"
            step="0.01"
            min="0"
            value={form.weight_kg}
            onChange={(e) => set("weight_kg", e.target.value)}
            placeholder="e.g. 12500"
            className="font-mono-data"
          />
        </Field>

        <Field id="thickness_mm" label="Thickness (mm)" error={errors.thickness_mm}>
          <Input
            id="thickness_mm"
            data-testid="thickness-input"
            type="number"
            step="0.01"
            min="0"
            value={form.thickness_mm}
            onChange={(e) => set("thickness_mm", e.target.value)}
            placeholder="e.g. 2.5"
            className="font-mono-data"
          />
        </Field>

        <Field id="width_mm" label="Width (mm)" error={errors.width_mm}>
          <Input
            id="width_mm"
            data-testid="width-input"
            type="number"
            step="0.1"
            min="0"
            value={form.width_mm}
            onChange={(e) => set("width_mm", e.target.value)}
            placeholder="e.g. 1250"
            className="font-mono-data"
          />
        </Field>

        <Field id="supplier" label="Supplier" error={errors.supplier}>
          <Input
            id="supplier"
            data-testid="supplier-input"
            value={form.supplier}
            onChange={(e) => set("supplier", e.target.value)}
            placeholder="e.g. ArcelorMittal"
          />
        </Field>

        <Field id="date_received" label="Date Received">
          <Input
            id="date_received"
            data-testid="date-received-input"
            type="date"
            value={form.date_received}
            onChange={(e) => set("date_received", e.target.value)}
          />
        </Field>
      </div>

      <Field id="notes" label="Notes (optional)">
        <Textarea
          id="notes"
          data-testid="notes-input"
          rows={2}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Inspection notes, damage report, etc."
        />
      </Field>

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-200">
        <Button
          type="submit"
          data-testid="submit-coil-btn"
          disabled={submitting}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11 px-6 font-semibold tracking-wide"
        >
          <Save className="h-4 w-4 mr-2" />
          {submitting ? "Saving…" : "Save to Inventory"}
        </Button>
      </div>
    </form>
  );
};

export default CoilForm;
